import { z } from "zod";
import { installmentSchema, installmentsBalanceSchema, parseApiResponse } from "@/api/responseSchemas";
import { apiClient } from "@/api/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/useAuthStore";
import { captureEntityMutation } from "@/analytics/posthog";

export interface Installment {
  id: number;
  user_id: number;
  account_id: number;
  name: string;
  created_at: string;
  updated_at: string;
  initial_amount: number;
  amount: number;
  description?: string | null;
  interest_rate: number | null;
  start_date?: string | null;
  num_payments?: number | null;
  done_payments: number;
  active: boolean;
  completed?: boolean | null;
  deleted_at?: string | null;
  monthly_payment?: number | null;
}

export interface CreateInstallmentRequest {
  name: string;
  account_id: number;
  description?: string;
  initial_amount?: number;
  amount?: number;
  interest_rate?: number | null;
  start_date?: string;
  num_payments?: number;
  done_payments?: number;
  active?: boolean;
}

export interface UpdateInstallmentRequest {
  name?: string;
  account_id?: number;
  description?: string | null;
  initial_amount?: number;
  amount?: number;
  interest_rate?: number | null;
  start_date?: string | null;
  num_payments?: number | null;
  done_payments?: number;
  active?: boolean;
  completed?: boolean;
}

export interface InstallmentPayment {
  id: number;
  amount: number;
  transaction_date: string;
  name?: string | null;
  description?: string | null;
}

export interface InstallmentBalanceItem {
  id: number;
  account_id: number;
  name: string;
  active: boolean;
  completed?: boolean | null;
  initial_amount?: number | null;
  amount: number;
  interest_rate: number | null;
  done_amount: number;
  amount_left: number;
  done_payments: number;
  done_payments_sum?: number;
  monthly_expected?: number | null;
  total_interest: number | null;
  remaining_interest: number | null;
  payoff_date?: string | null;
  start_date?: string | null;
  description?: string | null;
  num_payments?: number | null;
  payments: InstallmentPayment[];
}

export interface InstallmentsBalanceResponse {
  installments_balance: InstallmentBalanceItem[];
  total_monthly_expected: number;
  total_amount_installment: number;
  total_amount_pending: number;
  total_interest_pending: number;
  total_installments: number;
}

export const useInstallments = (options?: { enabled?: boolean }) => {
  const token = useAuthStore((state) => state.token);

  return useQuery({
    queryKey: ["installments"],
    queryFn: async () => {
      const res = await apiClient.get("/installments");
      return parseApiResponse(installmentSchema.array(), res.data, "GET /installments");
    },
    enabled: !!token && (options?.enabled ?? true),
  });
};

export const useInstallmentsBalance = () => {
  const token = useAuthStore((state) => state.token);

  return useQuery({
    queryKey: ["installmentsBalance"],
    queryFn: async () => {
      const res = await apiClient.get("/installments/balance");
      return parseApiResponse(installmentsBalanceSchema, res.data, "GET /installments/balance");
    },
    enabled: !!token,
  });
};

export const useCreateInstallment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (debt: CreateInstallmentRequest) => {
      const res = await apiClient.post("/installments", {
        name: debt.name,
        account_id: debt.account_id,
        description: debt.description ?? null,
        initial_amount: debt.initial_amount ?? 0,
        amount: debt.amount ?? 0,
        interest_rate: debt.interest_rate ?? null,
        start_date: debt.start_date ?? null,
        num_payments: debt.num_payments ?? null,
        done_payments: debt.done_payments ?? 0,
        active: debt.active ?? true,
      });
      return parseApiResponse(installmentSchema, res.data, "installment mutation");
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "installment", operation: "create", result: "success" });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "installment", operation: "create", result: "failure", error });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["installments"] });
      queryClient.invalidateQueries({ queryKey: ["installmentsBalance"] });
    },
  });
};

export const useUpdateInstallment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateInstallmentRequest }) => {
      const res = await apiClient.patch(`/installments/${id}`, data);
      // The endpoint answers with a boolean, not the updated installment. Parsing it as one
      // threw, which skipped onSuccess and left the list showing the old name.
      return parseApiResponse(z.boolean(), res.data, "PATCH /installments/{installment_id}");
    },
    onSuccess: () => {
      captureEntityMutation({ entity: "installment", operation: "update", result: "success" });
    },
    onError: (error) => {
      captureEntityMutation({ entity: "installment", operation: "update", result: "failure", error });
    },
    // onSettled, like the create mutation: a refetch after a failed write costs one request
    // and keeps the list honest, where onSuccess silently leaves it stale.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["installments"] });
      queryClient.invalidateQueries({ queryKey: ["installmentsBalance"] });
    },
  });
};

export const useDeleteInstallment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (installmentId: number) => {
      const res = await apiClient.delete(`/installments/${installmentId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["installments"] });
      queryClient.invalidateQueries({ queryKey: ["installmentsBalance"] });
    },
  });
};
